(ns serv-ui.prompts
  "The operator prompts window, at ops.kunagi.org - apart from the admin window.
  `#<id>` is one prompt, no hash the list of them. Sign-in is the admin
  window's (`serv-ui.login`); so is the key, which both keep in the same
  localStorage. All of it goes over one WebSocket (`serv-ui.live`)."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require ["react" :as react]
            ["react-dom/client" :as rdom]
            [clojure.string :as str]
            [kc.core :as kc]
            [serv-ui.api :as api]
            [serv-ui.auth :as auth]
            [serv-ui.live :as live]
            [serv-ui.login :as login]
            [serv-ui.mui :as mui :refer [sx]]
            [serv-ui.page :as page]
            [serv-ui.prompt-view :as view]
            [serv-ui.push :as push]
            [serv-ui.theme :as theme]
            [serv.prompt-markup :as markup]))

;; From public/ops/font/ (index.html): the window in JetBrains Mono (the
;; Nerd Font, for its icons), what an agent wrote in IBM Plex Sans
(def theme (theme/mk "dark" {:font view/mono}))
(def markdown-theme (theme/mk "dark" {:font "'IBM Plex Sans', system-ui, sans-serif"}))

;;; ------------------------------------------------------------- session

(defonce !session (atom {:phase :checking}))

(defn check! []
  (if-not (auth/signed-in?)
    (reset! !session {:phase :login})
    (-> (api/get> "/api/auth/me")
        (.then (fn [{:keys [user]}]
                 (reset! !session {:phase :ok :user user})
                 (push/sync!)))
        (.catch (fn [e]
                  (reset! !session (if (= 401 (:status (ex-data e)))
                                     {:phase :login}
                                     {:phase :error :error (ex-message e)})))))))

(defn sign-out! []
  (auth/forget-key!)
  (reset! !session {:phase :login}))

;;; ------------------------------------------------------------- location

(defn- hash-id [] (let [h (js/decodeURIComponent (subs (.-hash js/location) 1))]
                    (when-not (str/blank? h) h)))

(defonce !id (atom nil))

(defn goto! [id]
  (set! (.-hash js/location) (or id "")))

;;; ------------------------------------------------------------- keyboard
;;; The list: j/k or the arrows move, Enter opens. A prompt: the arrows
;;; scroll, Esc or Backspace goes back, a letter works a control - [s] submits, after
;;; an Enter to confirm; the others get a, b, c, ... without the s. Tab
;;; leaves a text field. A dialog or menu that is open keeps its keys.

(defonce !last-id (atom nil))
(defonce !confirming (atom false))

(defn- rows [] (vec (array-seq (js/document.querySelectorAll "[data-row]"))))

(defn- row-el [id] (some #(when (= id (.getAttribute ^js % "data-row")) %) (rows)))

(defn- focus-row! [^js el]
  (when el
    (.focus el #js {:preventScroll true})
    (.scrollIntoView el #js {:block "nearest" :behavior "smooth"})))

(def hotkey-letters "abcdefghijklmnopqrtuvwxyz")

(defn- hotkey-els [] (vec (array-seq (js/document.querySelectorAll "[data-hk-root] [data-hk]"))))

(defn label-hotkeys!
  "Each control its letter, in `data-hk-key`; the badge shows it."
  []
  (loop [[^js el & more] (hotkey-els), i 0]
    (when el
      (if (= "submit" (.getAttribute el "data-hk"))
        (do (.setAttribute el "data-hk-key" "s") (recur more i))
        (do (if (< i (count hotkey-letters))
              (.setAttribute el "data-hk-key" (nth hotkey-letters i))
              (.removeAttribute el "data-hk-key"))
            (recur more (inc i)))))))

(def hotkey-sx
  ;; the badges only where there is a keyboard
  (sx {"@media (hover: hover) and (pointer: fine)"
       {"& [data-hk-key]"        {:position "relative" :overflow "visible"}
        "& [data-hk-key]::after" {:content "attr(data-hk-key)" :position "absolute" :top -8 :right -8
                                  :zIndex 1 :minWidth 16 :height 16 :px "4px" :borderRadius "8px"
                                  :fontFamily view/mono :fontSize "0.6875rem"
                                  :fontWeight 600 :lineHeight "16px" :textAlign "center"
                                  :textTransform "none" :letterSpacing 0 :pointerEvents "none"
                                  :bgcolor "#4c566a" :color "#eceff4"}}}))

(defn- disabled? [^js el]
  (or (.-disabled el) (.contains (.-classList el) "Mui-disabled")
      (some? (.querySelector el ".Mui-disabled"))))

(defn- hotkey! [k]
  (when-let [^js el (if (= k "s")
                      (some #(when (= "submit" (.getAttribute ^js % "data-hk")) %) (hotkey-els))
                      (some #(when (= k (.getAttribute ^js % "data-hk-key")) %) (hotkey-els)))]
    (when-not (disabled? el)
      (case (.getAttribute el "data-hk")
        "submit" (reset! !confirming true)
        "field"  (when-let [^js in (.querySelector el "input:not([aria-hidden]), textarea:not([aria-hidden])")]
                   (.focus in #js {:preventScroll true})
                   (.scrollIntoView el #js {:block "nearest" :behavior "smooth"}))
        (.click el))
      true)))

;;; Smooth scrolling that keeps up with a held key: each press moves the
;;; target, a frame loop eases the page towards it.

(defonce !scroll (atom nil))

(defn- scroll-step! []
  (when-let [target @!scroll]
    (let [y  (.-scrollY js/window)
          dy (- target y)]
      (if (< (js/Math.abs dy) 1)
        (do (js/scrollTo 0 target) (reset! !scroll nil))
        (do (js/scrollTo 0 (+ y (* dy 0.18)))
            (js/requestAnimationFrame scroll-step!))))))

(defn- scroll-by! [dy]
  (let [running? (some? @!scroll)
        max-y    (- (.. js/document -documentElement -scrollHeight) (.-innerHeight js/window))
        from     (or @!scroll (.-scrollY js/window))]
    (reset! !scroll (-> (+ from dy) (max 0) (min max-y)))
    (when-not running? (js/requestAnimationFrame scroll-step!))))

(defn- text-input? [^js el]
  (and el (or (= "TEXTAREA" (.-tagName el))
              (and (= "INPUT" (.-tagName el))
                   (not (#{"checkbox" "radio" "button" "submit" "file"} (.-type el))))
              (.-isContentEditable el))))

(defn- on-key [^js e]
  (let [k      (.-key e)
        active (.-activeElement js/document)
        handle (fn [] (.preventDefault e))]
    (when (and (= :ok (:phase @!session))
               (not (or (.-ctrlKey e) (.-metaKey e) (.-altKey e)))
               ;; an open dialog or menu keeps its keys
               (nil? (js/document.querySelector ".MuiDialog-root, .MuiPopover-root")))
      (cond
        (text-input? active)
        (when (#{"Tab" "Escape"} k) (handle) (.blur active))

        (nil? @!id)
        (let [rs (rows)
              i  (.indexOf rs active)
              to (fn [j] (handle) (focus-row! (nth rs (-> j (max 0) (min (dec (count rs)))) nil)))]
          (when (seq rs)
            (case k
              ("j" "ArrowDown") (to (if (neg? i) 0 (inc i)))
              ("k" "ArrowUp")   (to (if (neg? i) 0 (dec i)))
              "Enter"           (when (neg? i) (handle) (.click (first rs)))
              nil)))

        :else
        (case k
          ("Escape" "Backspace") (do (handle) (goto! nil))
          "ArrowDown" (do (handle) (scroll-by! 120))
          "ArrowUp"   (do (handle) (scroll-by! -120))
          (when (and (re-matches #"[a-z]" k) (not (.-shiftKey e)) (hotkey! k))
            (handle)))))))

;;; ------------------------------------------------------------- toast

(defonce !toast (atom nil))

(def-ui Offline
  "Said while the socket is down; it reconnects by itself."
  []
  (let [state (kc/use-atom live/!state)]
    ($ mui/Snackbar {:open (= :lost state)
                     :anchorOrigin #js {:vertical "top" :horizontal "center"}}
       ($ mui/Alert {:severity "warning" :variant "filled"}
          "No connection to the server. Reconnecting..."))))

(def-ui Toast []
  (let [t (kc/use-atom !toast)]
    ($ mui/Snackbar {:open (boolean t) :autoHideDuration 4000
                     :anchorOrigin #js {:vertical "bottom" :horizontal "center"}
                     :onClose (fn [_ reason] (when-not (= "clickaway" reason) (reset! !toast nil)))}
       ($ mui/Alert {:severity "success" :variant "filled" :onClose (fn [_] (reset! !toast nil))}
          (or t "")))))

;;; ------------------------------------------------------------- list

(def state-colors {:open "primary" :answered "success" :rejected "error" :withdrawn "default" :expired "default"})

(def-ui StateChip [state]
  ($ mui/Chip {:size "small" :variant "outlined" :label (name (or state :open))
               :color (get state-colors state "default")}))

(def-ui StateIcon
  "A prompt's state, in the list's left column: open a dot - red while
  its agent is not accepted -, answered a tick, rejected a cross, withdrawn a struck circle."
  [state unaccepted?]
  ($ mui/Box {:sx (sx {:width 20 :height 20 :flexShrink 0 :display "grid" :placeItems "center"
                       :color (cond (not= :open state) "text.secondary"
                                    unaccepted?        "error.main"
                                    :else              "primary.main")})}
     (if-let [[d title] (case state
                          :answered  ["M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" "answered"]
                          :rejected  ["M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" "rejected"]
                          :withdrawn ["M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 2c1.85 0 3.55.63 4.9 1.69L5.69 16.9A8 8 0 0 1 12 4zm0 16c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1A8 8 0 0 1 12 20z" "withdrawn"]
                          nil)]
       ($ :svg {:viewBox "0 0 24 24" :width 20 :height 20 :fill "currentColor" :aria-label title :role "img"}
          ($ :title title)
          ($ :path {:d d}))
       ($ :svg {:viewBox "0 0 10 10" :width 10 :height 10 :fill "currentColor" :role "img"
                :aria-label (if unaccepted? "open, agent not accepted" "open")}
          ($ :circle {:cx 5 :cy 5 :r 5})))))

(defn- short-date
  "For the list: today the time, this year the day, else the day and year."
  [iso]
  (if-let [d (some-> iso js/Date.)]
    (let [now (js/Date.)]
      (cond (= (.toDateString d) (.toDateString now))
            (.toLocaleTimeString d js/undefined #js {:timeStyle "short"})
            (= (.getFullYear d) (.getFullYear now))
            (.toLocaleDateString d js/undefined #js {:month "short" :day "numeric"})
            :else
            (.toLocaleDateString d js/undefined #js {:dateStyle "medium"})))
    ""))

(defn- from-str
  "Who a prompt comes from: the agent as it described itself, or - for a
  prompt from before the agent keys - the user and device."
  [{:keys [from]}]
  (or (:agent from)
      (str (:user/email from) (when-let [d (:device from)] (str " (" d ")")))))

(def-ui NotifyBar
  "Above the list while the browser has not been asked: a way to turn on
  notifications for new prompts."
  []
  (let [permission (kc/use-atom push/!permission)]
    (when (= "default" permission)
      ($ mui/Alert {:severity "info" :variant "outlined" :sx (sx {:mb 3})
                    :action ($ mui/Button {:size "small" :color "inherit" :onClick (fn [_] (push/ask!))} "Turn on")}
         "Notifications for new prompts"))))

(def-ui PromptRow [p]
  (let [open?       (= :open (:state p))
        unaccepted? (and open? (false? (get-in p [:from :accepted])))]
    ($ mui/ListItemButton {:divider true :onClick (fn [_] (goto! (:id p)))
                           :data-row (:id p)
                           :sx (sx {:gap 2 :py 1.5 :alignItems "flex-start"
                                    "&:focus" {:bgcolor "action.selected" :outline "none"
                                               :boxShadow "inset 3px 0 0 var(--mui-palette-control-main, #d08770)"}})}
       ($ mui/Box {:sx (sx {:pt "2px"})}
          ($ StateIcon {:state (:state p) :unaccepted? unaccepted?}))
       ($ mui/Box {:sx (sx {:flexGrow 1 :minWidth 0})}
          ($ mui/Typography {:sx (sx {:fontWeight (if open? 500 400) :lineHeight 1.4
                                      :color (if open? "text.primary" "text.secondary")})}
             (:subject p))
          ($ mui/Box {:sx (sx {:display "flex" :gap 1 :alignItems "baseline" :mt 0.25
                               :fontSize "0.875rem" :color "text.secondary" :minWidth 0})}
             ($ :span {:style {:fontWeight 500 :flexShrink 0}}
                ($ mui/Box {:component "span" :sx (sx {:color (if open? "primary.main" "inherit")})}
                   (:project-name p)))
             (when unaccepted?
               ($ mui/Box {:component "span" :sx (sx {:color "error.main" :flexShrink 0})} "not accepted"))
             ($ :span {:style {:whiteSpace "nowrap" :overflow "hidden" :textOverflow "ellipsis"}}
                (from-str p))))
       ($ mui/Box {:component "time" :dateTime (:created p) :title (api/date-str (:created p))
                   :sx (sx {:flexShrink 0 :fontSize "0.8125rem" :color "text.secondary"
                            :fontVariantNumeric "tabular-nums" :lineHeight 1.75})}
          (short-date (:created p))))))

(def-ui Section [title ***]
  ($ mui/Typography {:component "h2"
                     :sx (sx {:fontSize "0.875rem" :fontWeight 600 :color "text.secondary"
                              :mt 4 :mb 1 :px 2 "&:first-of-type" {:mt 0}})}
     title))

(def-ui FirstSteps
  "Instead of an empty list: how to get a first prompt here."
  [user]
  (let [text (str "Try something: ask me a question with an operator prompt.\n"
                  "How it works: " js/location.origin "/api/operator-prompts\n"
                  "My address: " (:user/email user))
        [copied? set-copied] (kc/use-state false)]
    ($ mui/Box {:sx (sx {:px 2 :py 1})}
       ($ mui/Typography {:component "h2" :sx (sx {:fontSize "1.25rem" :fontWeight 500 :mb 1})}
          "Let your agents ask you")
       ($ mui/Typography {:sx (sx {:color "text.secondary" :mb 2})}
          "Tell your agent, in the chat you already have with it:")
       ($ mui/Paper {:variant "outlined"
                     :sx (sx {:p 2 :fontFamily "monospace" :fontSize "0.875rem" :lineHeight 1.7
                              :whiteSpace "pre-wrap" :overflowWrap "anywhere"})}
          text)
       ($ mui/Button {:variant "outlined" :size "small" :sx (sx {:mt 1.5})
                      :onClick (fn [_]
                                 (-> (js/navigator.clipboard.writeText text)
                                     (.then (fn [_] (set-copied true) (js/setTimeout #(set-copied false) 2000)))))}
          (if copied? "Copied" "Copy"))
       ($ mui/Typography {:sx (sx {:color "text.secondary" :mt 3})}
          "The prompt shows up here. The first time, you see which agent is asking, and you accept it once."))))

(def-ui PromptList [user]
  (let [load   (live/use-live "/api/operator-prompts")
        loaded (some? (get-in load [:data :prompts]))]
    (kc/use-effect [loaded]
      (when loaded (focus-row! (or (some->> @!last-id (row-el)) (first (rows))))))
    ($ page/Page {}
       ($ NotifyBar)
       (if-let [ps (get-in load [:data :prompts])]
         (if (empty? ps)
          ($ FirstSteps {:user user})
          (let [{open true settled false} (group-by #(= :open (:state %)) ps)]
           ($ :<>
              ($ Section {:title (if (seq open) (str "Waiting for you (" (count open) ")") "Waiting for you")})
              (if (seq open)
                ($ mui/List {:disablePadding true}
                   (for [p open] ($ PromptRow {:key (:id p) :p p})))
                ($ mui/Typography {:sx (sx {:color "success.main" :px 2 :py 1.5})}
                   "Nothing is waiting for you."))
              (when (seq settled)
                ($ :<>
                   ($ Section {:title "Settled"})
                   ($ mui/List {:disablePadding true}
                      (for [p settled] ($ PromptRow {:key (:id p) :p p}))))))))
         ($ page/Loading {:load load})))))

;;; ------------------------------------------------------------- one prompt

(def-ui SendIcon []
  ($ :svg {:viewBox "0 0 24 24" :width 20 :height 20 :fill "currentColor" :style {:marginRight 8}}
     ($ :path {:d "M2 21l21-9L2 3v7l15 2-15 2z"})))

(defn- answer-form [answer files]
  (let [f (js/FormData.)]
    (.append f "answer" (js/JSON.stringify (clj->js (apply dissoc answer (keys files)))))
    (doseq [[k fs] files, ^js file fs]
      (.append f (name k) file (.-name file)))
    f))

(def-ui AcceptBar [prompt reload]
  (let [[busy? set-busy] (kc/use-state false)
        [error set-error] (kc/use-state nil)]
    ($ mui/Alert {:severity "error" :variant "filled" :sx (sx {:mb 2})
                  :action ($ mui/Button {:color "inherit" :variant "outlined" :size "small"
                                         :disabled busy? :data-hk "click"
                                         :onClick (fn [_]
                                                    (set-busy true)
                                                    (-> (api/request> :post (str "/api/operator-prompts/"
                                                                                 (js/encodeURIComponent (:id prompt))
                                                                                 "/accept"))
                                                        (.then (fn [_] (reload)))
                                                        (.catch (fn [e] (set-busy false) (set-error (ex-message e))))))}
                            "Accept")}
       ($ mui/AlertTitle "An agent you have not accepted yet")
       ($ :div {:style {:whiteSpace "pre-wrap"}} (get-in prompt [:from :agent]))
       ($ :div {:style {:marginTop 8 :fontSize "0.85em"}}
          "Accept it to answer. From then on it can send you prompts without asking again.")
       (when error ($ :div {:style {:marginTop 8}} error)))))

(def-ui RejectDialog
  "Saying no to a prompt, with a message to the agent or without one."
  [prompt open? on-close]
  (let [[message set-message] (kc/use-state "")
        [busy? set-busy]      (kc/use-state false)
        [error set-error]     (kc/use-state nil)
        reject!               (fn []
                                (set-busy true)
                                (set-error nil)
                                (-> (api/request> :post (str "/api/operator-prompts/" (js/encodeURIComponent (:id prompt)) "/reject")
                                                  {:message message})
                                    (.then (fn [_]
                                             (set-busy false)
                                             (reset! !toast (str "Rejected: " (:subject prompt)))
                                             (goto! nil)))
                                    (.catch (fn [e] (set-busy false) (set-error (ex-message e))))))]
    ($ mui/Dialog {:open open? :fullWidth true :maxWidth "sm"
                   :onClose (fn [_] (when-not busy? (on-close)))}
       ($ mui/DialogTitle "Reject this prompt")
       ($ mui/DialogContent
          ($ mui/TextField {:label "Message to the agent (optional)" :multiline true :minRows 3
                            :fullWidth true :autoFocus true :margin "dense"
                            :value message :disabled busy?
                            :onChange (fn [^js e] (set-message (.. e -target -value)))})
          (when error ($ mui/Alert {:severity "error" :variant "outlined" :sx (sx {:mt 2})} error)))
       ($ mui/DialogActions {:sx (sx {:px 3 :pb 2})}
          ($ mui/Button {:onClick (fn [_] (on-close)) :disabled busy?} "Cancel")
          ($ mui/Button {:variant "contained" :color "error" :disableElevation true :disabled busy?
                         :startIcon (when busy? ($ mui/CircularProgress {:size 20 :color "inherit"}))
                         :onClick (fn [_] (reject!))}
             "Submit rejection")))))

(def-ui ConfirmDialog
  "Submitting by [s]: one more [Enter] sends it."
  [open? on-close on-confirm]
  ($ mui/Dialog {:open open? :maxWidth "xs" :fullWidth true :onClose (fn [_] (on-close))}
     ($ mui/DialogTitle "Send the answer?")
     ($ mui/DialogActions {:sx (sx {:px 3 :pb 2})}
        ($ mui/Button {:onClick (fn [_] (on-close))} "Cancel")
        ($ mui/Button {:variant "contained" :color "control" :disableElevation true :autoFocus true
                       :onClick (fn [_] (on-confirm))}
           "Submit"))))

(def-ui PromptForm [prompt reload]
  (let [{:keys [id meta markdown attachments state]} prompt
        tree       (react/useMemo #(markup/parse markdown) #js [markdown])
        controls   (react/useMemo #(markup/controls tree) #js [tree])
        fields     (react/useMemo #(markup/fields meta controls) #js [meta controls])
        open?      (= :open state)
        [answer set-answer] (kc/use-state (or (when-not open? (:answer prompt)) (markup/defaults meta controls)))
        [files set-files-all] (kc/use-state {})
        [busy? set-busy]    (kc/use-state false)
        [error set-error]   (kc/use-state nil)
        [rejecting? set-rejecting] (kc/use-state false)
        confirming? (kc/use-atom !confirming)
        ;; uploads count by the files chosen for them
        checked    (merge answer (into {} (for [[k f] fields :when (= "upload" (:kind f))]
                                            [k (mapv #(.-name ^js %) (get files k []))])))
        problems   (when open? (markup/check-answer meta controls checked))
        ctx        {:answer     answer
                    :fields     fields
                    :files      files
                    :read-only? (or (not open?) busy?)
                    :prompt-id  id
                    :attachments attachments
                    :set-value  (fn [k v] (set-answer #(assoc % k v)))
                    :set-files  (fn [k v] (set-files-all #(assoc % k v)))}
        submit!    (fn []
                     (set-busy true)
                     (set-error nil)
                     (-> (api/form> :post (str "/api/operator-prompts/" (js/encodeURIComponent id) "/answer")
                                    (answer-form answer files))
                         (.then (fn [_]
                                  (set-busy false)
                                  (reset! !toast (str "Answer sent: " (:subject prompt)))
                                  (goto! nil)))
                         (.catch (fn [e] (set-busy false) (set-error (ex-message e))))))]
    (kc/use-effect :always (label-hotkeys!))
    ($ :<>
       ($ mui/ThemeProvider {:theme markdown-theme}
          ($ view/Markdown {:tree tree :ctx ctx}))
       (when error
         ($ mui/Alert {:severity "error" :variant "outlined" :sx (sx {:mt 2})} error))
       (when open?
         ;; flush right, Reject before Submit; what is missing on the left
         ($ mui/Box {:sx (sx {:display "flex" :alignItems "center" :flexWrap "wrap" :gap 2 :mt 3})}
            (when (seq problems)
              ($ mui/Typography {:variant "body2" :sx (sx {:color "text.secondary"})}
                 (str "Still missing: " (str/join ", " (map #(first (str/split % #":")) problems)))))
            ($ mui/Box {:sx (sx {:display "flex" :gap 2 :ml "auto"})}
               ($ mui/Button {:variant "outlined" :color "error" :size "large" :disabled busy? :data-hk "click"
                              :onClick (fn [_] (set-rejecting true))}
                  "Reject")
               ($ mui/Button {:variant "contained" :size "large" :disableElevation true :color "control" :data-hk "submit"
                              :disabled (or busy? (boolean (seq problems)) (false? (get-in prompt [:from :accepted])))
                              :startIcon (if busy? ($ mui/CircularProgress {:size 20 :color "inherit"}) ($ SendIcon))
                              :sx (sx {"&.Mui-disabled" {:bgcolor "action.disabledBackground" :color "text.secondary"}})
                              :onClick (fn [_] (submit!))}
                  "Submit"))
            ($ RejectDialog {:prompt prompt :open? rejecting? :on-close #(set-rejecting false)})
            ($ ConfirmDialog {:open? confirming? :on-close #(reset! !confirming false)
                              :on-confirm (fn [] (reset! !confirming false) (submit!))}))))))

(def-ui MenuIcon []
  ($ :svg {:viewBox "0 0 24 24" :width 24 :height 24 :fill "currentColor"}
     ($ :path {:d "M3 6h18v2H3zm0 5h18v2H3zm0 5h18v2H3z"})))

(def header-height 56)

(def-ui Header
  "The fixed bar on top: on the list the app's name, on a prompt its
  project and subject; the menu on the right."
  [user overline title]
  (let [[anchor set-anchor] (kc/use-state nil)
        close               (fn [] (set-anchor nil))]
    ($ :<>
       ($ mui/Paper {:square true :elevation 3
                     :sx (sx {:position "fixed" :top 0 :left 0 :right 0 :zIndex 1100
                              :height header-height :display "flex" :alignItems "center"
                              :px 2 :gap 1})}
          (if title
            ($ mui/Box {:sx (sx {:flexGrow 1 :minWidth 0})}
               (when overline
                 ($ mui/Typography {:noWrap true
                                    :sx (sx {:display "block" :fontSize "0.8125rem" :fontWeight 500
                                             :lineHeight 1.4 :color "primary.main"})}
                    overline))
               ($ mui/Typography {:noWrap true :sx (sx {:fontWeight 500 :lineHeight 1.3
                                                        :fontSize (if overline "1rem" "1.15rem")})}
                  title))
            ($ mui/Typography {:variant "h6" :sx (sx {:flexGrow 1 :cursor "pointer" :whiteSpace "nowrap"})
                               :onClick (fn [_] (goto! nil))}
               "Operator prompts"))
          ($ mui/IconButton {:aria-label "menu" :edge "end" :color "inherit"
                             :onClick (fn [^js e] (set-anchor (.-currentTarget e)))}
             ($ MenuIcon)))
       ($ mui/Box {:sx (sx {:height header-height})})
       ($ mui/Menu {:anchorEl anchor :open (boolean anchor) :onClose close
                    :anchorOrigin (sx {:vertical "bottom" :horizontal "right"})
                    :transformOrigin (sx {:vertical "top" :horizontal "right"})}
          ($ mui/MenuItem {:onClick (fn [_] (close) (goto! nil))} "All prompts")
          ($ mui/MenuItem {:component "a" :href "/api/operator-prompts" :target "_blank"
                           :rel "noopener" :onClick close}
             "Agent API")
          ($ mui/Divider)
          ($ mui/MenuItem {:disabled true} (:user/email user))
          ($ mui/Divider)
          ($ mui/MenuItem {:onClick (fn [_] (close) (sign-out!))} "Sign out")
          ($ mui/Divider)
          ($ mui/MenuItem {:disabled true :sx (sx {:fontSize "0.75rem"})} (api/build))))))

(def-ui SettledBar
  "Above a prompt that is no longer open: what became of it, and when."
  [prompt]
  (case (:state prompt)
    :answered  ($ mui/Alert {:severity "success" :variant "filled" :sx (sx {:mb 2})}
                  (str "Answered " (api/date-str (:answered prompt))))
    :rejected  ($ mui/Alert {:severity "error" :variant "filled" :sx (sx {:mb 2})}
                  (str "Rejected " (api/date-str (:rejected prompt)))
                  (when-let [m (:message prompt)]
                    ($ :div {:style {:marginTop 4 :whiteSpace "pre-wrap"}} m)))
    :withdrawn ($ mui/Alert {:severity "warning" :variant "filled" :sx (sx {:mb 2})}
                  (str "Withdrawn by the agent " (api/date-str (:withdrawn prompt))))
    nil))

(def-ui Prompt [id user]
  (let [load (live/use-live (str "/api/operator-prompts/" (js/encodeURIComponent id)))
        p    (:data load)]
    (if-not p
      ($ :<>
         ($ Header {:user user :title ""})
         ($ page/Page
            (if (= 404 (:status load))
              ($ mui/Alert {:severity "warning" :variant "outlined"} "No such prompt, or not for you.")
              ($ page/Loading {:load load}))))
      ($ :<>
       ($ Header {:user user :overline (:project-name p) :title (:subject p)})
       ($ page/Page {:top ($ SettledBar {:prompt p}) :subtitle (:description p)}
         ($ mui/Box {:sx (sx {:display "flex" :columnGap 1.5 :rowGap 0.5 :alignItems "center" :flexWrap "wrap"
                              :mb 2 :color "text.secondary" :fontSize "0.875rem"})}
            (when (= :open (:state p)) ($ StateChip {:state :open}))
            ($ :span (str "from " (from-str p)))
            ($ :time {:dateTime (:created p) :style {:fontVariantNumeric "tabular-nums"}}
               (api/date-str (:created p))))
         ($ mui/Divider {:sx (sx {:mb 2})})
         ($ mui/Box {:data-hk-root true :sx hotkey-sx}
            (when (and (= :open (:state p)) (false? (get-in p [:from :accepted])))
              ($ AcceptBar {:prompt p :reload (:reload load)}))
            ($ PromptForm {:key (str (:id p) (:state p)) :prompt p :reload (:reload load)})))))))

;;; ------------------------------------------------------------- root

(def-ui Centered [***]
  ($ mui/Box {:sx (sx {:minHeight "100vh" :display "flex" :flexDirection "column" :gap 2
                       :alignItems "center" :justifyContent "center" :padding "1rem"})}
     ***))

(def-ui Root []
  (let [session (kc/use-atom !session)
        id      (kc/use-atom !id)
        _       (kc/use-atom api/!signed-out)]
    ($ mui/ThemeProvider {:theme theme}
       ($ mui/CssBaseline)
       ($ Offline)
       (case (:phase session)
         :login    ($ login/Login {:on-login (fn [] (push/ask!) (check!)) :prompt-id id :title "Operator prompts"})
         :checking ($ Centered ($ mui/CircularProgress {:size 28}))
         :error    ($ Centered
                      ($ mui/Alert {:severity "error" :variant "outlined"} (:error session))
                      ($ mui/Button {:variant "outlined" :onClick (fn [_] (check!))} "Retry"))
         :ok       ($ :<>
                      (if id
                        ($ Prompt {:key id :id id :user (:user session)})
                        ($ :<>
                           ($ Header {:user (:user session)})
                           ($ PromptList {:user (:user session)})))
                      ($ Toast))))))

(defonce ROOT
  (delay (rdom/createRoot (js/document.getElementById "root"))))

(defn render! [] (.render @ROOT ($ Root)))

(defn after-load! [] (render!))

(defn main! []
  (reset! !id (hash-id))
  (.addEventListener js/window "hashchange"
                     (fn [_]
                       (when-let [id @!id] (reset! !last-id id))
                       (reset! !id (hash-id))
                       (reset! !scroll nil)
                       (reset! !confirming false)
                       (js/scrollTo 0 0)))
  (.addEventListener js/window "keydown" on-key)
  (add-watch api/!signed-out ::login (fn [_ _ _ _] (reset! !session {:phase :login})))
  (live/start!)
  (check!)
  (render!))
